import { useState, useMemo } from 'react'
import { Box, Breadcrumbs, Card, Divider, IconButton, Typography } from '@mui/material'
import { Button } from '@/@core/components/mui/Button'
import { useTranslator, useRouteQuery, useNotification } from '@/hooks'
import { FormProvider, useForm } from 'react-hook-form'
import { formatMonthDisplay } from '@/utils/formatDate'
import { AttendanceReconcileTable } from '../tables/AttendanceReconcileTable'
import {
  Attendances,
  IHttpError,
  useAttendanceConfirmReconcileByCompanyIdEmployeeMutation,
  useAttendanceByCompanyEmployeeBulkMutation
} from '@/@data'
import { useQuery } from '@tanstack/react-query'
import { TableLimit } from '@/@core/components/mui/TableLimit'
import { InputField } from '@/components/common/input/InputField'
import { useParams, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { AttendanceNotSavedDialog } from '../dialog/AttendanceNotSavedDialog'
import Link from '@/components/Link'
import { ConfirmVerifyReconciliationDialog } from '../dialog/ConfirmVerifyReconciliationDialog'

export const CompanyManualReconcile = ({
  company,
  setManualReconcile,
  onSuccess
}: {
  company: any
  setManualReconcile: (manualReconcile: boolean) => void
  onSuccess: () => void
}) => {
  const { __ } = useTranslator()
  const n = useNotification()
  const { searchQuery, updateQuery } = useRouteQuery()
  const { id } = useParams<{ id: string }>()

  const [isSetting, setIsSetting] = useState(true)
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set())
  const [openNotSavedDialog, setOpenNotSavedDialog] = useState(false)
  const [openConfirmVerifyReconciliationDialog, setOpenConfirmVerifyReconciliationDialog] = useState(false)

  // State bulk API
  const [bulkChanges, setBulkChanges] = useState<{
    [attendanceId: string]: {
      attendance_id: number
      attendance: Array<{
        date: number
        day_of_month?: string
        status: string
      }>
    }
  }>({})

  const monthParam = searchQuery.get('month')
  const { monthName, dateRange } = formatMonthDisplay(monthParam)

  console.log(dateRange)

  const handleToggleSetting = () => {
    setIsSetting(!isSetting)
  }

  const methods = useForm<any>({
    defaultValues: {
      month: '',
      is_all: 0,
      ids: [],
      exclude_ids: [],
      status: undefined,
      start_date: '',
      end_date: ''
    }
  })

  const { mutate: mutateBulk, isPending: isBulkPending } = useAttendanceByCompanyEmployeeBulkMutation()

  const onSubmit = async () => {
    const bulkPayload = {
      month: searchQuery.get('month') || '',
      employees: Object.values(bulkChanges)
    }

    mutateBulk(
      {
        companyId: Number(searchQuery.get('companyId')) || Number(id),
        data: bulkPayload
      },
      {
        onSuccess: () => {
          n.success(__('Attendance updated successfully'))
          refetchEmployee()
          setManualReconcile(false)
          setBulkChanges({}) // Clear bulk changes
          setSelectedUsers(new Set()) // Clear selected users
          updateQuery({
            user_name_reconcile: '',
            page: 1,
            start_date: undefined,
            end_date: undefined
          })
          onSuccess()
        },
        onError: (error: IHttpError) => {
          n.error(error.message)
        }
      }
    )
  }

  // Handle checkbox changes from table
  const handleCheckboxChange = (
    _newIsAllChecked: boolean,
    _newExcludedUsers: Set<number>,
    newSelectedUsers: Set<number>,
    isAll: number,
    excludeUserIds: number[],
    userIds: number[]
  ) => {
    // Update local state
    setSelectedUsers(newSelectedUsers)

    // Update form values
    methods.setValue('is_all', isAll)
    methods.setValue('exclude_ids', excludeUserIds)
    methods.setValue('ids', userIds)
  }

  // Check if there are pending changes
  const hasPendingChanges = Object.keys(bulkChanges).length > 0

  const paramsEmployee = useMemo(() => {
    return {
      companyId: Number(searchQuery.get('companyId')) || Number(id),
      month: searchQuery.get('month'),
      user_keyword: searchQuery.get('user_keyword_reconcile'),
      page: searchQuery.get('page'),
      perPage: searchQuery.get('perPage')
    }
  }, [id, searchQuery])

  const { data: dataEmployee, refetch: refetchEmployee } = useQuery({
    ...Attendances.getAttendanceByCompanyIdEmployee(paramsEmployee),
    enabled: (!!id && !!searchQuery.get('month')) || !!searchQuery.get('companyId')
  })

  const paramsReconcileById = useMemo(() => {
    return {
      companyId: Number(searchQuery.get('companyId')) || Number(id),
      month: searchQuery.get('month')
    }
  }, [id, searchQuery.get('month')])

  const { data: dataReconcileById } = useQuery({
    ...Attendances.getAttendanceDetailByCompanyId(paramsReconcileById),
    enabled: (!!id && !!searchQuery.get('month')) || !!searchQuery.get('companyId')
  })

  const currentMonth = dayjs(monthParam)

  const minDate = dataReconcileById?.data?.attendance?.startDate
    ? dayjs(dataReconcileById.data.attendance.startDate).format('YYYY-MM-DD')
    : currentMonth.startOf('month').format('YYYY-MM-DD')

  const maxDate = dataReconcileById?.data?.attendance?.endDate
    ? dayjs(dataReconcileById.data.attendance.endDate).format('YYYY-MM-DD')
    : currentMonth.endOf('month').format('YYYY-MM-DD')

  const { mutate: mutateConfirmReconcile, isPending: isConfirmReconcilePending } =
    useAttendanceConfirmReconcileByCompanyIdEmployeeMutation()

  const handleConfirmReconcileAction = () => {
    const payload = {
      month: searchQuery.get('month') || ''
    }

    mutateConfirmReconcile(
      {
        companyId: Number(searchQuery.get('companyId')) || Number(id),
        data: payload
      },
      {
        onSuccess: () => {
          n.success(__('Attendance reconcile confirmed successfully'))
          refetchEmployee()
          setManualReconcile(false)
          onSuccess()
        },
        onError: (error: IHttpError) => {
          n.error(error.message)
        }
      }
    )
  }

  // Handle dialog confirm - proceed with navigation/close
  const handleConfirmDialog = () => {
    setOpenNotSavedDialog(false)
    setManualReconcile(false)
    updateQuery({
      user_name_reconcile: '',
      page: 1,
      start_date: undefined,
      end_date: undefined
    })
    navigate(-1)
  }

  const navigate = useNavigate()

  const hasChanges = () => {
    const hasDateRange = searchQuery.get('start_date') && searchQuery.get('end_date')
    const hasStatus = methods.getValues('status')

    return !!(hasDateRange && hasStatus)
  }

  // Handle navigation with unsaved changes check
  const handleNavigateBack = () => {
    if (hasChanges()) {
      setOpenNotSavedDialog(true)
    } else {
      navigate(-1)
    }
  }

  // Handle close with unsaved changes check
  const handleClose = () => {
    if (hasChanges()) {
      setOpenNotSavedDialog(true)
    } else {
      setManualReconcile(false)
      updateQuery({
        user_name_reconcile: '',
        page: 1,
        start_date: undefined,
        end_date: undefined
      })
    }
  }

  return (
    <>
      <Box>
        <Box
          component={'div'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 5
          }}
        >
          {<i className='tabler-chevron-left cursor-pointer' onClick={handleNavigateBack}></i>}

          <Typography variant='h3'>{__('Company Attendance Edit')}</Typography>

          <Divider orientation='vertical' flexItem />

          <Breadcrumbs aria-label='breadcrumb' separator={<i className='tabler-chevron-right' />}>
            <Link color='inherit' href='/cms/attendance'>
              <i className='tabler-home' />
            </Link>
            <Link color='inherit' href='/cms/attendance'>
              {__('Attendance Management')}
            </Link>
            <div className='cursor-pointer text-[#3551f2]' onClick={handleClose}>
              {__('Company Attendance Detail')}
            </div>
            <Typography sx={{ color: 'text.primary' }}>{__('Company Attendance Edit')}</Typography>
          </Breadcrumbs>
        </Box>
      </Box>

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className='flex justify-between items-start gap-5'>
            <Card sx={{ borderRadius: 3, flex: 1 }}>
              <div className='p-4'>
                <div className='flex justify-between items-center mb-4'>
                  <div className='flex items-center gap-3'>
                    <Button
                      variant='outlined'
                      size='small'
                      className='border-blue-500 text-blue-600 hover:border-blue-600'
                    >
                      <span
                        className='text-[15px] font-medium max-w-[500px] whitespace-nowrap overflow-hidden text-ellipsis'
                        title={company?.name || '-'}
                      >
                        {company?.name || '-'}
                      </span>
                    </Button>
                    <Divider orientation='vertical' flexItem className='w-[2px] bg-[#2f2b3d1f]' />
                    <Typography variant='h6' className='text-[18px] font-medium'>
                      {__('Attendance Reconcile')}
                    </Typography>
                  </div>
                  <div className='flex items-center gap-2'>
                    <IconButton
                      size='small'
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: '#f5f5f5',
                        '&:hover': {
                          backgroundColor: '#e0e0e0'
                        }
                      }}
                      onClick={handleClose}
                    >
                      <i className='tabler-x text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size='small'
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: '#f5f5f5',
                        '&:hover': {
                          backgroundColor: '#e0e0e0'
                        }
                      }}
                      onClick={() => {
                        handleToggleSetting()
                      }}
                    >
                      <i className='tabler-viewport-wide text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                    </IconButton>
                  </div>
                </div>
                {/*  */}
                <Divider />
                <div className='flex justify-between items-center py-3'>
                  <div className='flex items-center gap-3'>
                    <div className='flex items-center gap-3'>
                      <Typography variant='h6' className='text-gray-800 font-medium text-[24px]'>
                        {monthName || '-'}
                      </Typography>
                    </div>
                  </div>
                  <div className='flex items-center gap-3'>
                    <Button
                      type='button'
                      variant='outlined'
                      size='small'
                      className='border-blue-500 text-blue-600 hover:border-blue-600'
                    >
                      {/* <span className='text-[15px] font-medium'>{dateRange || '-'}</span> */}
                      <span className='text-[15px] font-medium'>
                        {dayjs(dataReconcileById?.data?.attendance?.startDate).format('DD/MM/YYYY')} -{' '}
                        {dayjs(dataReconcileById?.data?.attendance?.endDate).format('DD/MM/YYYY')}
                      </span>
                    </Button>
                  </div>
                </div>
                <Divider />

                <div className='py-5 flex justify-between items-center'>
                  <div className='flex items-center gap-3'>
                    <div className='w-[80px]'>
                      <TableLimit />
                    </div>
                    <div className='flex items-center gap-3'>
                      <div className='flex items-center gap-1'>
                        <div className='w-[16px] h-[16px] rounded-full bg-[#577dfd]'></div>
                        <Typography variant='body2' className='text-[15px] font-medium text-[#2f2b3de6]'>
                          {__('Working Day')}
                        </Typography>
                      </div>
                      <div className='flex items-center gap-1'>
                        <div className='w-[16px] h-[16px] rounded-full bg-[#ff7074]'></div>
                        <Typography variant='body2' className='text-[15px] font-medium text-[#2f2b3de6]'>
                          {__('Absent Day')}
                        </Typography>
                      </div>
                      <div className='flex items-center gap-1'>
                        <div className='w-[16px] h-[16px] rounded-full bg-[#c0bec5]'></div>
                        <Typography variant='body2' className='text-[15px] font-medium text-[#2f2b3de6]'>
                          {__('Non-Working Day')}
                        </Typography>
                      </div>
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <div className='w-[240px]'>
                      <InputField
                        placeholder={__('Search user')}
                        value={searchQuery.get('user_keyword_reconcile') || ''}
                        onChange={e => {
                          updateQuery({ user_keyword_reconcile: e.target.value, page: 1 })
                        }}
                        className='w-full'
                      />
                    </div>
                  </div>
                </div>
                <AttendanceReconcileTable
                  data={dataEmployee?.data || []}
                  dataNormal={dataReconcileById?.data}
                  meta={dataEmployee?.meta || {}}
                  refetch={refetchEmployee}
                  onCheckboxChange={handleCheckboxChange}
                  selectedUsers={selectedUsers}
                  methods={methods}
                  minDate={minDate}
                  maxDate={maxDate}
                  attendanceAssumption={dataReconcileById?.data}
                  bulkChanges={bulkChanges}
                  setBulkChanges={setBulkChanges}
                  onSuccess={() => {
                    refetchEmployee()
                  }}
                />
              </div>
            </Card>
            {isSetting && (
              <Card sx={{ borderRadius: 3, width: '266px' }}>
                <div className='p-4'>
                  <Button
                    variant='tonal'
                    type='button'
                    size='small'
                    className='block w-full mb-3'
                    onClick={() => {
                      setSelectedUsers(new Set())
                      setBulkChanges({})
                      methods.reset()
                      updateQuery({
                        start_date: undefined,
                        end_date: undefined,
                        user_keyword_reconcile: undefined
                      })
                    }}
                  >
                    <span className='text-[15px] font-medium'>{__('Reset')}</span>
                  </Button>
                  <Button
                    type='submit'
                    variant='tonal'
                    size='small'
                    className='block w-full bg-[#00bad129] mb-3'
                    disabled={Object.keys(bulkChanges).length === 0 || isBulkPending}
                  >
                    <span className='text-[15px] font-medium text-[#00bad1]'>{__('Save & Update')}</span>
                  </Button>

                  <Button
                    type='button'
                    variant='tonal'
                    size='small'
                    className='block w-full bg-[#28c76f29]'
                    disabled={
                      hasPendingChanges ||
                      isConfirmReconcilePending ||
                      !dataReconcileById?.data?.is_allow_confirm_reconcile
                    }
                    onClick={() => {
                      setOpenConfirmVerifyReconciliationDialog(true)
                    }}
                  >
                    <span className='text-[15px] font-medium text-[#28c76f]'>{__('Confirm Reconcile')}</span>
                  </Button>
                </div>
              </Card>
            )}
          </div>
        </form>
      </FormProvider>

      <ConfirmVerifyReconciliationDialog
        open={openConfirmVerifyReconciliationDialog}
        onClose={() => {setOpenConfirmVerifyReconciliationDialog(false)}}
        onConfirm={() => {
          setOpenConfirmVerifyReconciliationDialog(false)
          handleConfirmReconcileAction()
        }}
      />

      <AttendanceNotSavedDialog
        open={openNotSavedDialog}
        onClose={() => setOpenNotSavedDialog(false)}
        onConfirm={handleConfirmDialog}
      />
    </>
  )
}
